import { useMutation, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from 'emotion-theming';
import { Box, Flex, Text } from 'rebass';
import { ResultOf } from 'gql.tada';
import { Checkbox } from '../../../../components/Form';
import Spinner from '../../../../components/Spinner/Spinner';
import { Theme } from '../../../../theme';
import {
  Table,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from '../../../../components/Table/Table';
import useNotify from '../../../../hooks/useNotify';
import { gql } from '../../gql';

const GET_NOTIFICATION_PREFERENCES = gql(`
  query GetNotificationPreferences {
    notificationPreferences {
      notificationType
      description
      supportedChannels
      enabledChannels
    }
  }
`);

const SAVE_NOTIFICATION_PREFERENCE = gql(`
  mutation SaveNotificationPreference($input: NotificationPreferenceInput!) {
    saveNotificationPreference(input: $input) {
      notificationType
      description
      supportedChannels
      enabledChannels
    }
  }
`);

type NotificationPreferencesResult = ResultOf<
  typeof GET_NOTIFICATION_PREFERENCES
>;
type NotificationPreference =
  NotificationPreferencesResult['notificationPreferences'][number];
type NotificationChannel = NotificationPreference['supportedChannels'][number];

const channels: {
  channel: NotificationChannel;
  label: string;
}[] = [
  { channel: 'IN_APP', label: 'In-app' },
  { channel: 'EMAIL', label: 'Email' },
  { channel: 'PUSH', label: 'Mobile' },
];

const StyledTable = styled(Table)`
  flex-grow: 0;
  width: max-content;
  max-width: 100%;

  input[type='checkbox'] {
    margin: 0;
    padding: 0;
  }
`;

const StyledTableRow = styled(TableRow)`
  cursor: default;

  &:hover {
    cursor: default;
  }
`;

const notificationColumnWidth = 400;
const channelColumnWidth = 64;

const SmallCheckbox = styled(Checkbox)`
  width: 13px;
  height: 13px;
`;

const NotificationPreferences = (): JSX.Element => {
  const theme = useTheme() as Theme;
  const { showError } = useNotify();
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);

  const { data, loading, refetch } =
    useQuery<NotificationPreferencesResult>(GET_NOTIFICATION_PREFERENCES, {
      fetchPolicy: 'network-only',
      onError: useCallback((error) => {
        showError('Error loading notification preferences', error);
      }, []),
    });

  useEffect(() => {
    if (data?.notificationPreferences) {
      setPreferences([...data.notificationPreferences]);
    }
  }, [data]);

  const [saveNotificationPreference, { loading: saving }] = useMutation(
    SAVE_NOTIFICATION_PREFERENCE,
    {
      onError: (error) => {
        showError('Error saving notification preference', error);
        refetch();
      },
    }
  );

  const handlePreferenceChange = useCallback(
    (
      preference: NotificationPreference,
      channel: NotificationChannel,
      enabled: boolean
    ) => {
      setPreferences((current) =>
        current.map((item) =>
          item.notificationType === preference.notificationType
            ? {
                ...item,
                enabledChannels: enabled
                  ? Array.from(new Set([...item.enabledChannels, channel]))
                  : item.enabledChannels.filter((value) => value !== channel),
              }
            : item
        )
      );

      saveNotificationPreference({
        variables: {
          input: {
            notificationType: preference.notificationType,
            channel,
            enabled,
          },
        },
        onCompleted: (result) => {
          const savedPreference = result.saveNotificationPreference;
          setPreferences((current) =>
            current.map((item) =>
              item.notificationType === savedPreference.notificationType
                ? savedPreference
                : item
            )
          );
        },
      });
    },
    [saveNotificationPreference]
  );

  if (loading && !preferences.length) {
    return (
      <Text variant="body2" color="fieldBorderHover">
        Loading notification preferences...
      </Text>
    );
  }

  if (!preferences.length) {
    return (
      <Text variant="body2" color="fieldBorderHover">
        No notification preferences are available.
      </Text>
    );
  }

  return (
    <StyledTable>
      <TableHeaderRow role="row">
        <TableHeaderCell
          role="columnheader"
          style={{ width: notificationColumnWidth }}
        >
          <Flex alignItems="center" sx={{ gap: 2 }}>
            Notification
            {saving && (
              <Box
                as="span"
                aria-label="Saving notification preferences"
                sx={{
                  flexShrink: 0,
                  lineHeight: 0,
                  display: 'inline-flex',
                }}
                >
                  <Spinner
                    size={11.2}
                    color={theme.colors.primary}
                    backgroundColor={theme.colors.base.gray[200]}
                  />
              </Box>
            )}
          </Flex>
        </TableHeaderCell>
        {channels.map(({ channel, label }) => (
          <TableHeaderCell
            key={channel}
            role="columnheader"
            style={{
              width: channelColumnWidth,
              justifyContent: 'center',
            }}
          >
            {label}
          </TableHeaderCell>
        ))}
      </TableHeaderRow>
      {preferences.map((preference) => (
        <StyledTableRow key={preference.notificationType} role="row">
          <TableCell
            role="cell"
            style={{
              width: notificationColumnWidth,
              whiteSpace: 'normal',
              lineHeight: 1.35,
            }}
          >
            {preference.description}
          </TableCell>
          {channels.map(({ channel, label }) => {
            const supported = preference.supportedChannels.includes(channel);
            const enabled = preference.enabledChannels.includes(channel);
            const togglePreference = () =>
              handlePreferenceChange(preference, channel, !enabled);
            const handleCellClick = (event: React.MouseEvent) => {
              event.stopPropagation();
              if (
                supported &&
                (event.target as HTMLElement).tagName !== 'INPUT'
              ) {
                togglePreference();
              }
            };

            return (
              <TableCell
                key={channel}
                role="cell"
                style={{
                  width: channelColumnWidth,
                  display: 'flex',
                  justifyContent: 'center',
                }}
                onClick={supported ? handleCellClick : undefined}
              >
                {supported && (
                  <SmallCheckbox
                    variant="forms.checkbox"
                    checked={enabled}
                    title={`${label} notifications`}
                    onChange={(event) => {
                      event.stopPropagation();
                      togglePreference();
                    }}
                  />
                )}
              </TableCell>
            );
          })}
        </StyledTableRow>
      ))}
    </StyledTable>
  );
};

export default NotificationPreferences;
